import * as React from 'react';
import { Direction } from '@westwing/ui-kit/Icons/StyledIcons';
import { HorizontalScrollableContainer } from '@westwing/ui-kit/ScrollableContainer';
import { CarouselWrapper, ContentWrapper, LeftArrow, RightArrow } from './ProjectSlider.styles';

export type CarouselProps = {
    content: React.ReactNode[] | React.ReactNode;
    onNext?: () => void;
    onPrevious?: () => void;
};

/**
 * This component is the same as UI Kit's Slider, but with slightly different
 * scrolling behavior.
 * UI Kit says scrolling is not testable with Jest, so ignoring it here as well.
 * Since Slider is tested in UI Kit, I think we can safely ignore this component.
 */
/* istanbul ignore next */
function Carousel({ content, onNext, onPrevious }: CarouselProps) {
    const scrollableContainerRef = React.useRef<HTMLDivElement>(null);
    const [buttonConfig, setButtonConfig] = React.useState({ left: false, right: true });

    const updateButtonConfig = React.useCallback((_evt?: unknown, scrollLeft?: number) => {
        const outerContainer = scrollableContainerRef.current;
        const innerContainer = outerContainer?.children?.[0];
        if (innerContainer) {
            const scrollLeftMax = innerContainer.scrollWidth - innerContainer.clientWidth;
            const scrollLeftCurrent = scrollLeft || innerContainer.scrollLeft;
            setButtonConfig(() => {
                let [left, right] = [true, true];
                if (scrollLeftCurrent <= scrollLeftMax * 0.01) {
                    [left, right] = [false, true];
                }
                if (scrollLeftCurrent >= scrollLeftMax * 0.99) {
                    [left, right] = [true, false];
                }
                return { left, right };
            });
        }
    }, []);

    const onSlideChange = React.useCallback(
        (direction: Direction) => {
            const outerContainer = scrollableContainerRef.current;
            const innerContainer = outerContainer?.children?.[0];
            if (innerContainer) {
                const carouselItem = innerContainer.children[Math.floor(innerContainer.children.length / 2)];
                const style = window.getComputedStyle(carouselItem, null) || ({} as CSSStyleDeclaration);
                const margins = parseInt(style?.marginRight || '0', 10) + parseInt(style?.marginLeft || '0', 10);
                const step = (carouselItem.clientWidth + margins) * (direction === Direction.Left ? -1 : 1);
                const updatedScrollLeft = innerContainer.scrollLeft + step;
                innerContainer.scrollLeft = updatedScrollLeft;
                updateButtonConfig(null, updatedScrollLeft);
            }
        },
        [updateButtonConfig]
    );

    const handleClick = (direction: Direction) => () => {
        onSlideChange(direction);
        if (direction === Direction.Right && onNext) {
            onNext();
        } else if (direction === Direction.Left && onPrevious) {
            onPrevious();
        }
    };

    React.useEffect(() => {
        const outerContainer = scrollableContainerRef.current;
        const innerContainer = outerContainer?.children?.[0] as HTMLDivElement;
        if (!innerContainer) {
            return () => undefined;
        }

        innerContainer.addEventListener('wheel', updateButtonConfig);
        return () => innerContainer.removeEventListener('wheel', updateButtonConfig);
    }, [updateButtonConfig]);

    return (
        <CarouselWrapper>
            <ContentWrapper ref={scrollableContainerRef}>
                <HorizontalScrollableContainer scrollBehavior="smooth">{content}</HorizontalScrollableContainer>
            </ContentWrapper>
            <LeftArrow
                hide={!buttonConfig.left}
                onClick={handleClick(Direction.Left)}
                data-testid="left-arrow-proj-slider"
            />
            <RightArrow
                hide={!buttonConfig.right}
                onClick={handleClick(Direction.Right)}
                data-testid="right-arrow-proj-slider"
            />
        </CarouselWrapper>
    );
}

export default Carousel;
